import { useEffect, useState, Suspense } from 'react';
import { HashRouter as Router } from 'react-router-dom';
import './dx-styles.scss';
import './themes/generated/theme.base.css';
import './themes/generated/theme.additional.css';

import 'react-toastify/dist/ReactToastify.css';
import './scss/otk-styles.scss';
import LoadPanel from 'devextreme-react/load-panel.js';
import { NavigationProvider } from 'contexts/navigation';
import { AuthProvider, useAuth } from 'contexts/auth';
import { useScreenSizeClass } from 'utils/media-query';
import { Content } from './Content';
import { UnauthenticatedContent } from './UnauthenticatedContent';

import uaMessages from 'contexts/ua.json';
import { MessageProvider } from 'contexts/messages/messages';
import { NotificationManager } from 'components';
import { News } from 'pages/news/news';
import { loadMessages, locale } from 'devextreme/localization';

import { Helmet, HelmetProvider } from 'react-helmet-async';
import { FAVICON, TITLE } from 'app-constants';

loadMessages(uaMessages);
locale('uk');

const AppHead = (): JSX.Element => (
  <Helmet>
    <link rel="icon" href={FAVICON} />
    <link rel="apple-touch-icon" href={FAVICON} />
    <title>{TITLE}</title>
  </Helmet>
);

const App = (): JSX.Element => {

  const { user, loading, reAuth } = useAuth();
  const [ firstStart, setFirstStart ] = useState(false);

  useEffect(() => {
    const chkUser = async() => {
      if (!user) {
        try {
          const res = await reAuth();
          setFirstStart(!(res && res.isOk));
        } catch (err) {
          setFirstStart(true);
        }
      }
    };

    chkUser();

  }, [ reAuth, user ]);

  if (loading) return <LoadPanel visible={true} />;

  if (user) {
    if (firstStart) return <News onApprove={() => setFirstStart(false)} />;

    return (
      <HelmetProvider>
        <AppHead />
        <MessageProvider>
          <NotificationManager />
          <Suspense fallback={<LoadPanel />}>
            <Content />
          </Suspense>
        </MessageProvider>
      </HelmetProvider>
    );
  }

  return (
    <HelmetProvider>
      <AppHead />
      <UnauthenticatedContent />
    </HelmetProvider>
  );
};

const AppMain = (): JSX.Element => {
  const screenSizeClass = useScreenSizeClass();
  return (
    <Router future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
      <AuthProvider>
        <NavigationProvider>
          <div className={`app ${screenSizeClass}`}>
            <App/>
          </div>
        </NavigationProvider>
      </AuthProvider>
    </Router>
  );
};


export default AppMain;
